import React, { useEffect, useMemo, useState } from "react";
import { recruitmentProcess } from "./CreateRoleForm";
import { Combobox } from "@headlessui/react";
import {
  ChevronUpDownIcon,
  CheckIcon,
  PlusIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import { format } from "path";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  AssessmentQuestionnaire,
  AssessmentSlot,
  TemplateQuestionnaires,
} from "../../../Models";
import { SortOptionTemplate } from "../../Template/AssessmentTemplates";
import { v4 as uuidv4 } from "uuid";
import { PencilIcon, XCircleIcon } from "@heroicons/react/20/solid";
import { fetchAssessmentQuestionnairesByTemplate } from "../../../api/assessmenttemplate.api";
import useApiToken from "../../../hooks/useApiToken";
// import ReactDatePicker from "react-datepicker";

function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(" ");
}

type SlotTab = "active" | "expired" | "inactive";

// Three distinct states a recruiter cares about, kept apart because the reason a slot is out of
// play changes what you do about it: an expired slot was fine and simply ran its course, while a
// non-active one was switched off on purpose with the red button.
//
// Deactivation wins when a slot is both switched off AND past its end time - the deliberate action
// is the more meaningful fact about that slot.
//
// endTime is wrapped in new Date() because slots loaded from the API arrive as strings while slots
// added in this form are real Dates.
const classifySlot = (slot: AssessmentSlot, now: number): SlotTab => {
  if (slot.isActive === false) return "inactive";
  return new Date(slot.endTime).getTime() > now ? "active" : "expired";
};

interface CreateAssessmentSlotsProps {
  processes: recruitmentProcess[];
  slots: AssessmentSlot[];
  setSlots: React.Dispatch<React.SetStateAction<AssessmentSlot[]>>;
  selectedProcess: recruitmentProcess | null;
  setSelectedProcess: React.Dispatch<
    React.SetStateAction<recruitmentProcess | null>
  >;
}

const CreateAssessmentSlots: React.FC<CreateAssessmentSlotsProps> = ({
  processes,
  slots,
  setSlots,
  selectedProcess,
  setSelectedProcess,
}) => {

  const token = useApiToken();
  const [processQuery, setProcessQuery] = useState("");
  const [editingSlotId, setEditingSlotId] = useState<string | null>(null);
  const [editedSlot, setEditedSlot] = useState<any>(null);
  const [slotTab, setSlotTab] = useState<SlotTab>("active");

  const slotsByTab = useMemo(() => {
    const now = Date.now();
    const buckets: Record<SlotTab, AssessmentSlot[]> = {
      active: [],
      expired: [],
      inactive: [],
    };
    slots.forEach((slot) => buckets[classifySlot(slot, now)].push(slot));
    return buckets;
  }, [slots]);

  const visibleSlots = slotsByTab[slotTab];

  const [newSlotStartTime, setNewSlotStartTime] = useState<Date>(new Date());
  const [newSlotEndTime, setNewSlotEndTime] = useState<Date>(
    new Date(Date.now() + 60 * 60 * 1000)
  );

  const [questionnaires, setQuestionnaires] = useState<
    AssessmentQuestionnaire[]
  >([]);
  const [selectedQuestionnaire, setSelectedQuestionnaire] =
    useState<AssessmentQuestionnaire | null>();
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const eligibleProcesses = processes.filter(
    (process) =>
     ( process.assessmentTemplateId !== null || process.singleAssesmentId !== null ) &&
      process.isTestSlotsAvailable === true
  );


  useEffect(() => {
    const fetchQuestionnaires = async () => {
      if (!token) return;
      if (selectedProcess?.assessmentTemplateId) {
        setLoading(true);
        try {
          const result = await fetchAssessmentQuestionnairesByTemplate(
            selectedProcess.assessmentTemplateId,
            token
          );
          setQuestionnaires(result || []);
        } catch (error) {
          console.error("Error fetching questionnaires:", error);
        } finally {
          setLoading(false);
        }
      }
    };

    fetchQuestionnaires();
  }, [selectedProcess]);

  useEffect(() => {
    setSelectedQuestionnaire(null);
  }, [selectedProcess]);



  const addNewSlot = () => {
    setError(null);

    if (!selectedProcess) {
      setError("Please select a process.");
      return;
    }

    if (!selectedQuestionnaire) {
      setError("Please select an assessment questionnaire.");
      return;
    }
    if (!selectedQuestionnaire.time) {
      setError("The selected questionnaire does not have a defined duration.");
      return;
    }
    if (!newSlotStartTime || isNaN(new Date(newSlotStartTime).getTime())) {
      setError("Please select a valid date and time for the slot.");
      return;
    }
    const calculatedEndTime = new Date(
      newSlotStartTime.getTime() +
        selectedQuestionnaire.time * 60 * 1000 +
        5 * 60 * 1000
    );

    // if (newSlotEndTime <= newSlotStartTime) {
    //   setError("End time must be after start time.");
    //   return;
    // }

    const newSlot: AssessmentSlot = {
      id: uuidv4(),
      roleStatusId: selectedProcess.id,
      roleStatusName : selectedProcess.title,
      assessmentQuestionnaireId: selectedQuestionnaire.id,
      assessmentQuestionnaire: selectedQuestionnaire,
      startTime: newSlotStartTime,
      endTime: calculatedEndTime,
      isActive: true,
    };

    

    setSlots([...slots, newSlot]);
    // Jump to whichever tab the new slot landed on. A slot added with a start time in the past is
    // classified expired immediately, and silently filing it behind another tab would read as
    // "my slot was not added".
    setSlotTab(classifySlot(newSlot, Date.now()));
    setNewSlotStartTime(new Date(calculatedEndTime.getTime()));
    setNewSlotEndTime(new Date(calculatedEndTime.getTime() + 60 * 60 * 1000));
  };

  const getDuration = (start: Date, end: Date) => {
    const diffMs = end.getTime() - start.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    return `${Math.floor(diffMins / 60)}h ${diffMins % 60}m`;
  };
  const removeSlot = (slotId: string) => {
    setSlots(slots.filter((slot) => slot.id !== slotId));
  };
  const filteredProcesses =
    processQuery === ""
      ? eligibleProcesses
      : eligibleProcesses.filter((process) => {
          return process.title
            .toLowerCase()
            .includes(processQuery.toLowerCase());
        });

  const handleSaveClick = (slotId: string) => {
    if (!editedSlot || isNaN(new Date(editedSlot.startTime).getTime())) {
      setError("Please select a valid date and time for the slot.");
      return;
    }
    const updatedSlots = slots.map((slot) =>
      slot.id === slotId ? { ...slot, ...editedSlot } : slot
    );
    setSlots(updatedSlots);
    setEditingSlotId(null);
  };
  const handleIsActiveSlot = (slotId: string) => {
    const updatedSlots = slots.map((slot) =>
      slot.id === slotId ? { ...slot, isActive: false } : slot
    );
    setSlots(updatedSlots);
  };

  const handleCancelClick = () => {
    setEditingSlotId(null);
    setEditedSlot(null);
  };
  const handleEditSlotClick = (slot: AssessmentSlot) => {
    setEditingSlotId(slot.id);
    setEditedSlot({ ...slot });
  };
  useEffect(() => {
    if (selectedProcess) {
      if (selectedProcess.singleAssesmentId != null) {
        //individual assesment initialistion
        setSelectedQuestionnaire(selectedProcess.singleAssessment);
      } else {
        // Assessment Set it will be setted by dropdown
        setSelectedQuestionnaire(null);
      }
    }
  }, [selectedProcess]);
  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-100 text-red-700 p-2 rounded-md">{error}</div>
      )}
      <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6">
        <label className="block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5 dark:text-gray-400">
          Assessment Process
        </label>
        <div className="mt-2 sm:col-span-2 sm:mt-0">
          <Combobox
            as="div"
            value={selectedProcess}
            onChange={(process) => {
              setSelectedProcess(process);
            }}
          >
            <div className="relative">
              <Combobox.Input
                className="w-full rounded-md border-0 bg-white py-1.5 pl-3 pr-10 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-1 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6 dark:bg-transparent dark:text-gray-300 dark:ring-gray-500"
                placeholder="Select Process with Available Test Slots"
                onChange={(event) => setProcessQuery(event.target.value)}
                displayValue={(process: recruitmentProcess | null) =>
                  process?.title || ""
                }
              />
              <Combobox.Button className="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-none">
                <ChevronUpDownIcon
                  className="h-5 w-5 text-gray-400"
                  aria-hidden="true"
                />
              </Combobox.Button>
              {filteredProcesses.length > 0 && (
                <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm dark:bg-darkbglight">
                  {filteredProcesses.map((process) => (
                    <Combobox.Option
                      key={process.id}
                      value={process}
                      className={({ active }) =>
                        classNames(
                          "relative cursor-default select-none py-2 pl-3 pr-9",
                          active
                            ? "bg-entntblue text-white"
                            : "text-gray-900 dark:text-gray-300"
                        )
                      }
                    >
                      {({ active, selected }) => (
                        <>
                          <span
                            className={classNames(
                              "block truncate",
                              selected ? "font-semibold" : ""
                            )}
                          >
                            {process.title}
                          </span>
                          {selected && (
                            <span
                              className={classNames(
                                "absolute inset-y-0 right-0 flex items-center pr-4",
                                active ? "text-white" : "text-entntblue"
                              )}
                            >
                              <CheckIcon
                                className="h-5 w-5"
                                aria-hidden="true"
                              />
                            </span>
                          )}
                        </>
                      )}
                    </Combobox.Option>
                  ))}
                </Combobox.Options>
              )}
            </div>
          </Combobox>
        </div>
      </div>
      {selectedProcess && (
        <>
          <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6">
            <label className="block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5 dark:text-gray-400">
              Test Slots <span className="text-red-500">*</span>
            </label>
            <div className="mt-2 sm:col-span-2 sm:mt-0">
              <div className="flex flex-col md:flex-row gap-4 items-center">
                <div className="flex-1">
                  <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-400">
                    Questionnaire
                    {selectedProcess.singleAssesmentId != null && (
                      <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">
                        (Pre-selected)
                      </span>
                    )}
                  </label>
                  {}
                  <Combobox
                    value={
                      selectedQuestionnaire ?? selectedProcess.singleAssessment
                    }
                    onChange={(value) => {
                      // Only allow changes for single assessments
                      if (selectedProcess.singleAssesmentId == null) {
                        setSelectedQuestionnaire(value);
                      } else
                        setSelectedQuestionnaire(
                          selectedProcess.singleAssessment
                        );
                    }}
                    disabled={selectedProcess.singleAssesmentId != null}
                  >
                    <div className="relative">
                      <Combobox.Input
                        className="w-full rounded-md border-0 bg-white py-1.5 pl-3 pr-10 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-1 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6 dark:bg-transparent dark:text-gray-300 dark:ring-gray-500"
                        placeholder="Select Questionnaire"
                        displayValue={(q: AssessmentQuestionnaire | null) =>
                          q?.title || ""
                        }
                      />
                      <Combobox.Button className="absolute inset-y-0 right-0 flex items-center px-2 focus:outline-none">
                        <ChevronUpDownIcon
                          className="h-5 w-5 text-gray-400"
                          aria-hidden="true"
                        />
                      </Combobox.Button>
                      {questionnaires.length > 0 && (
                        <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm dark:bg-darkbglight">
                          {questionnaires.map((q) => (
                            <Combobox.Option
                              key={q.id}
                              value={q}
                              className={({ active }) =>
                                classNames(
                                  "relative cursor-default select-none py-2 pl-3 pr-9",
                                  active
                                    ? "bg-entntblue text-white"
                                    : "text-gray-900 dark:text-gray-300"
                                )
                              }
                            >
                              {({ active, selected }) => (
                                <>
                                  <span
                                    className={classNames(
                                      "block truncate",
                                      selected ? "font-semibold" : ""
                                    )}
                                  >
                                    {q.title}
                                  </span>
                                  {selected && (
                                    <span
                                      className={classNames(
                                        "absolute inset-y-0 right-0 flex items-center pr-4",
                                        active ? "text-white" : "text-entntblue"
                                      )}
                                    >
                                      <CheckIcon
                                        className="h-5 w-5"
                                        aria-hidden="true"
                                      />
                                    </span>
                                  )}
                                </>
                              )}
                            </Combobox.Option>
                          ))}
                        </Combobox.Options>
                      )}
                    </div>
                  </Combobox>
                </div>

                <div className="flex-1">
                  <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-400">
                    Start Time
                  </label>
                  <DatePicker
                    selected={newSlotStartTime}
                    onChange={(date: Date) => setNewSlotStartTime(date)}
                    showTimeSelect
                    timeFormat="HH:mm"
                    timeIntervals={15}
                    dateFormat="MMMM d, yyyy h:mm aa"
                    className="w-full rounded-md border-0 py-1.5 pl-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-1 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6 dark:bg-transparent dark:text-gray-300 dark:ring-gray-500"
                  />
                </div>

                {/* <div className="flex-1">
                  <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-400">
                    End Time
                  </label>
                  <DatePicker
                    selected={newSlotEndTime}
                    onChange={(date: Date) => setNewSlotEndTime(date)}
                    disabled={true}
                    showTimeSelect
                    timeFormat="HH:mm"
                    timeIntervals={15}
                    dateFormat="MMMM d, yyyy h:mm aa"
                    className="w-full rounded-md border-0 py-1.5 pl-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-1 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6 dark:bg-transparent dark:text-gray-300 dark:ring-gray-500"
                  />
                </div> */}

                <div className="flex items-end">
                  <button
                    type="button"
                    onClick={addNewSlot}
                    className="mt-6 inline-flex items-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                  >
                    <PlusIcon
                      className="-ml-0.5 mr-1.5 h-5 w-5"
                      aria-hidden="true"
                    />
                    Add
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
      {slots.length > 0 && (
        <div className="mt-4">
          <div className="mb-3 border-b border-gray-200 dark:border-gray-700">
            <nav className="-mb-px flex gap-6" aria-label="Slot status tabs">
              {([
                { key: "active" as SlotTab, label: "Active" },
                { key: "expired" as SlotTab, label: "Expired" },
                { key: "inactive" as SlotTab, label: "Non-Active" },
              ]).map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => {
                    setSlotTab(tab.key);
                    // Editing state belongs to a row that may not exist on the other tab.
                    setEditingSlotId(null);
                    setEditedSlot(null);
                  }}
                  aria-current={slotTab === tab.key ? "page" : undefined}
                  className={`whitespace-nowrap border-b-2 px-1 pb-2 text-sm font-medium transition-colors ${
                    slotTab === tab.key
                      ? "border-entntblue text-entntblue dark:text-blue-400 dark:border-blue-400"
                      : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                  }`}
                >
                  {tab.label}
                  <span
                    className={`ml-2 rounded-full px-2 py-0.5 text-xs font-semibold ${
                      slotTab === tab.key
                        ? "bg-blue-50 text-entntblue dark:bg-blue-900/30 dark:text-blue-300"
                        : "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300"
                    }`}
                  >
                    {slotsByTab[tab.key].length}
                  </span>
                </button>
              ))}
            </nav>
          </div>

          {visibleSlots.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400 py-3">
              {slotTab === "active"
                ? "No active slots. Check the Expired and Non-Active tabs."
                : slotTab === "expired"
                ? "No expired slots."
                : "No deactivated slots."}
            </p>
          ) : (
          <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
            <table className="min-w-full divide-y divide-gray-300 dark:divide-gray-700 text-sm">
              <thead className="bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-200">
                <tr>
                  <th className="py-2 px-3 text-left font-semibold">#</th>
                  <th className="py-2 px-3 text-left font-semibold">
                    Questionnaire
                  </th>
                  <th className="py-2 px-3 text-left font-semibold">
                    Assessment Step
                  </th>
                  <th className="py-2 px-3 text-left font-semibold">
                    Start Time
                  </th>
                  <th className="py-2 px-3 text-left font-semibold">
                    End Time
                  </th>
                  <th className="py-2 px-3 text-left font-semibold">
                    Slot Duration
                  </th>
                  <th className="py-2 px-3 text-left font-semibold">
                    Test Duration
                  </th>
                  <th className="py-2 px-3 text-left font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-200">
                {visibleSlots.map((slot, index) => (
                  <tr key={slot.id}>
                    <td className="py-2 px-3">{index + 1}</td>

                    <td className="py-2 px-3">
                      {
                      slot.individualAssessmentId != null && editingSlotId === slot.id ? (
                        <select
                          className="border-0 p-1 rounded min-w-32 bg-white py-1.5 pl-3 pr-10 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-1 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6 dark:bg-transparent dark:text-gray-300 dark:ring-gray-500"
                          value={editedSlot?.assessmentQuestionnaire?.id || ""}
                          onChange={(e) => {
                            const selectedQ = questionnaires.find(
                              (q) => q.id === e.target.value
                            );

                            if (!selectedQ) return;

                            const newEndTime = editedSlot?.startTime
                              ? new Date(
                                  editedSlot.startTime.getTime() +
                                    selectedQ.time * 60 * 1000 +
                                    5 * 60 * 1000
                                )
                              : null;

                            setEditedSlot((prev: any) => ({
                              ...prev,
                              assessmentQuestionnaire: selectedQ,
                              assessmentQuestionnaireId: selectedQ.id,
                              roleStatusId: selectedProcess?.id,
                              endTime: newEndTime ?? prev?.endTime,
                            }));
                          }}
                        >
                          <option value="">
                            Select Questionnaire
                          </option>
                          {questionnaires.map((q) => (
                            <option
                              key={q.id}
                              value={q.id}
                              className="bg-white text-gray-900 dark:text-gray-300"
                            >
                              {q.title}
                            </option>
                          ))}
                        </select>
                      ) : (
                        slot.assessmentQuestionnaire?.title
                      )}
                    </td>
                    <td className="py-2 px-3">{slot.roleStatus?.name || slot.roleStatusName }</td>
                    <td className="py-2 px-3">
                      {editingSlotId === slot.id ? (
                        <DatePicker
                          selected={editedSlot?.startTime}
                          onChange={(date: Date) => {
                            if (
                              !date ||
                              isNaN(new Date(editedSlot.startTime).getTime())
                            ) {
                              return;
                            }
                            const calculatedEndTime = new Date(
                              date.getTime() +
                                (editedSlot?.assessmentQuestionnaire
                                  ? editedSlot?.assessmentQuestionnaire?.time *
                                    60 *
                                    1000
                                  : 0) +
                                5 * 60 * 1000
                            );

                            setEditedSlot({
                              ...editedSlot,
                              startTime: date,
                              endTime: calculatedEndTime,
                            });
                          }}
                          showTimeSelect
                          timeFormat="HH:mm"
                          timeIntervals={15}
                          dateFormat="MMMM d, yyyy h:mm aa"
                          className="w-full rounded-md border-0 py-1.5 pl-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-1 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6 dark:bg-transparent dark:text-gray-300 dark:ring-gray-500"
                        />
                      ) : (
                        slot.startTime.toLocaleString()
                      )}
                    </td>

                    <td className="py-2 px-3">
                      {editingSlotId === slot.id ? (
                        <DatePicker
                          selected={editedSlot?.endTime}
                          onChange={(date: Date) => {
                            if (
                              !date ||
                              isNaN(new Date(editedSlot.startTime).getTime())
                            ) {
                              return;
                            }
                            setEditedSlot({ ...editedSlot, endTime: date });
                          }}
                          disabled={true}
                          showTimeSelect
                          timeFormat="HH:mm"
                          timeIntervals={15}
                          dateFormat="MMMM d, yyyy h:mm aa"
                          className="w-full rounded-md border-0 py-1.5 pl-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-1 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6 dark:bg-transparent dark:text-gray-300 dark:ring-gray-500"
                        />
                      ) : (
                        slot.endTime.toLocaleString()
                      )}
                    </td>
                    <td className="py-2 px-3">
                      {getDuration(slot.startTime, slot.endTime)}
                    </td>
                    <td className="py-2 px-3">
                      {slot.assessmentQuestionnaire?.time ?? "N/A"}
                    </td>

                    <td className="py-2 px-3 flex space-x-2">
                      {editingSlotId === slot.id ? (
                        <>
                          <button
                            type="button"
                            onClick={() => handleSaveClick(slot.id)}
                            disabled={!(editedSlot?.assessmentQuestionnaire)}
                            className={` px-2 py-1 text-xs text-white rounded ${editedSlot?.assessmentQuestionnaire ? "cursor-auto bg-entntblue" : "cursor-not-allowed bg-gray-600 "}`}
                          >
                            Save
                          </button>
                          <button
                            type="button"
                            onClick={handleCancelClick}
                            className="bg-gray-600 px-2 py-1 text-xs text-white rounded hover:bg-gray-500"
                          >
                            Cancel
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            type="button"
                            onClick={() => handleEditSlotClick(slot)}
                            className="bg-blue-600 px-2 py-1 text-xs text-white rounded hover:bg-blue-500"
                          >
                            <PencilIcon
                              className="h-4 w-4"
                              aria-hidden="true"
                            />
                          </button>
                          {slot.isLocked ? (
                            <button
                              type="button"
                              onClick={() => {
                                handleIsActiveSlot(slot.id);
                              }}
                              title={
                                slot.isActive
                                  ? "Candidates who booked this slot will get a email to book slots again and the current slot will no longer be active."
                                  : ""
                              }
                              className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-semibold text-white shadow-sm cursor-pointer bg-red-600 hover:bg-red-500 }`}
                            >
                              {slot.isActive ? (
                                <XCircleIcon
                                  className="h-4 w-4"
                                  aria-hidden="true"
                                />
                              ) : (
                                <div>Not Active</div>
                              )}
                            </button>
                          ) : (
                            <button
                              type="button"
                              onClick={() => removeSlot(slot.id)}
                              disabled={slot.isLocked}
                              className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-semibold text-white shadow-sm  ${slot.isLocked ? "cursor-not-allowed bg-gray-600 " : "cursor-auto bg-red-600 hover:bg-red-500"}`}
                            >
                              <TrashIcon
                                className="h-4 w-4"
                                aria-hidden="true"
                              />
                            </button>
                          )}
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CreateAssessmentSlots;
